import * as React from "react";
import { useHistory } from "react-router";
import { useRecoilState } from "recoil";

import { Loading } from "@/components/Loading";
import { ManagementDetailCashflow } from "@/components/management/Cashflow";
import { DetailNavigation } from "@/components/management/DetailNavigation";
import { ManagementDetailView } from "@/components/management/ManagementDetailView";
import { ManagementDisplayControl, ManagementDisplayMode } from "@/components/management/ManagementDisplayControl";
import { ManagementDetailNearBy } from "@/components/management/Nearby";
import { ChangePropertyImageModal } from "@/components/management/property_actions/ChangePropertyImageModal";
import { useManagementDetailActions } from "@/components/management/property_actions/useManagementDetailActions";
import { ManagementDetailSimulation } from "@/components/management/Simulation";
import { SimulationSettings } from "@/components/management/SimulationSettings";
import { useDevice } from "@/hooks/useDevice";
import { useManagementDetailData } from "@/hooks/useManagementDetailData";
import { usePropertyViews } from "@/hooks/usePropertyViews";
import { BaseTemplate } from "@/layouts/BaseTemplate";
import { isPropertyCurrentInUse } from "@/utilities/calculateProperty";
import { NavigationPath } from "@/utilities/enums";
import { PropertyTourSeenState } from "@/view_models/atoms";

import { ManagementDetailPropertyInfo } from "./components/items/PropertyInfo";
import AppTour from "./components/Tour";

const propertySteps = [
  {
    target: "#management-tour-view",
    content: "表示は「簡易版」と「詳細版」で\n切り替えられます。",
    disableBeacon: true,
    placement: "bottom-start" as const,
  },
  {
    target: "#management-tour-cashflow",
    content: "今月の収支は、家賃から\nローンと経費を引いた金額です。",
    disableBeacon: true,
    placement: "top-start" as const,
  },
];

export const ManagementDetailPage = () => {
  const history = useHistory();
  const [tourSeen, setTourSeen] = useRecoilState(PropertyTourSeenState);
  const [stepIndex, setStepIndex] = React.useState(0);
  const [isTourReady, setIsTourReady] = React.useState(false);
  const [displayMode, setDisplayMode] = React.useState<ManagementDisplayMode>("detail");
  const completedTourRef = React.useRef<string | null>(null);

  const { isMobile } = useDevice();
  const imageActions = useManagementDetailActions();
  const { isLoading, error, property, cashflow_result } = useManagementDetailData();
  const { selectedView, handleViewChange } = usePropertyViews();

  const isMultipleRoom = property?.property.property_category === "一棟物件";
  const tourType = isMultipleRoom ? "multipleRoom" : "singleRoom";
  const isCurrentInUse = isMultipleRoom ? true : isPropertyCurrentInUse(property?.lease_contracts ?? []);
  const shouldShowTour =
    selectedView === "operation" &&
    isTourReady &&
    property?.property.is_new_label &&
    !tourSeen[tourType] &&
    isCurrentInUse;
  const steps = propertySteps;

  React.useEffect(() => {
    try {
      const raw = window.localStorage.getItem("propally:property_tour_seen:v1");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (JSON.stringify(parsed) !== JSON.stringify(tourSeen)) {
          setTourSeen(parsed);
        }
      }
    } catch (_) {
      // noop
    } finally {
      setIsTourReady(true);
    }
  }, [setTourSeen, tourSeen]);

  React.useEffect(() => {
    if (shouldShowTour) {
      completedTourRef.current = null;
      setStepIndex(0);
    }
  }, [shouldShowTour, steps.length]);

  const handleTourComplete = React.useCallback(() => {
    if (!shouldShowTour || completedTourRef.current === tourType) return;
    completedTourRef.current = tourType;
    // ツアーが完了したら、状態を更新し二回目は表示しない
    setTourSeen((prev) => ({
      ...prev,
      [tourType]: true,
    }));
  }, [setTourSeen, tourType, shouldShowTour]);

  const advanceTour = React.useCallback(() => {
    if (stepIndex >= steps.length - 1) {
      handleTourComplete();
      return;
    }
    setStepIndex((prev) => {
      return prev === stepIndex ? prev + 1 : prev;
    });
  }, [handleTourComplete, steps.length, stepIndex]);

  if (error) throw error;
  if (isLoading || !property || !cashflow_result) return <Loading />;

  const navigation = (
    <DetailNavigation
      selectedView={selectedView}
      onChange={handleViewChange}
      building={isMultipleRoom}
      onShowDetail={displayMode === "simple" ? () => setDisplayMode("detail") : undefined}
    />
  );

  // 選択されたボタンに応じて表示内容を切り替える
  const renderViewContent = () => {
    switch (selectedView) {
      case "operation":
        return <ManagementDetailCashflow navigation={navigation} displayMode={displayMode} />;
      case "property": // 検討中と異なる表示
        return <ManagementDetailPropertyInfo />;
      case "simulation":
        return <ManagementDetailSimulation />;
      case "nearby":
        return <ManagementDetailNearBy propertyId={property.property.id} />;
      default:
        return <ManagementDetailCashflow navigation={navigation} displayMode={displayMode} />;
    }
  };

  return (
    <BaseTemplate
      headerTitle={
        isMobile
          ? selectedView === "operation"
            ? "物件詳細"
            : selectedView === "property"
            ? "物件情報"
            : selectedView === "simulation"
            ? "シミュレーション"
            : "周辺の成約事例"
          : undefined
      }
      onBack={
        isMobile
          ? () => (selectedView === "operation" ? history.push("/management/own") : handleViewChange("operation"))
          : undefined
      }
      navigationPath={NavigationPath.PROPERTY_MANAGEMENT_OWNED}
      isDense
      isPcV2
    >
      <AppTour
        run={shouldShowTour}
        steps={steps}
        stepIndex={stepIndex}
        onNext={advanceTour}
        onComplete={handleTourComplete}
      />
      <ManagementDetailView
        propertyType="保有中"
        propertyHolder={property}
        monthlyBalanceSheet={cashflow_result}
        navigation={navigation}
        selectedView={selectedView}
        displayControl={
          selectedView === "operation" ? (
            <ManagementDisplayControl value={displayMode} onChange={setDisplayMode} />
          ) : undefined
        }
        sidebarContent={
          selectedView === "simulation" ? (
            <SimulationSettings onCustomize={() => history.push("/management/detail/simulation?custom=1")} />
          ) : undefined
        }
        onPhotoChange={imageActions.openImageModal}
        onViewBack={() => handleViewChange("operation")}
        onBack={() => history.push("/management/own")}
      >
        {renderViewContent()}
      </ManagementDetailView>
      <ChangePropertyImageModal
        open={imageActions.openChangeImagePropertyModal}
        onClose={imageActions.closeImageModal}
        onClickChangeImage={(file) => imageActions.handleUpdateImage(property.property.id, file)}
      />
    </BaseTemplate>
  );
};
